import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Eye,
  Mail,
  User,
  Building,
  Send,
  X,
  CheckCircle,
  AlertTriangle
} from 'lucide-react';

/**
 * EmailTemplatePreview - Shows a preview of what the email will look like
 * @param {string} type - Type of email: 'student_note', 'agent_status', 'application_status'
 * @param {object} data - Data to populate the template
 * @param {function} onClose - Callback when preview is closed
 * @param {function} onConfirm - Callback when user confirms sending
 */
const EmailTemplatePreview = ({ type, data, onClose, onConfirm }) => {
  const [sending, setSending] = useState(false);

  const handleConfirm = async () => {
    setSending(true);
    try {
      await onConfirm();
    } finally {
      setSending(false);
    }
  };

  // Generate email content based on type
  const getEmailContent = () => {
    switch (type) {
      case 'student_note':
        return {
          subject: 'New Message from H E Direct Consultants',
          recipient: data.studentEmail,
          recipientName: data.studentName,
          body: (
            <>
              <p style={{ margin: '0 0 16px 0', color: '#333' }}>Dear {data.studentName},</p>
              <div style={{ 
                background: '#f8f9fa', 
                padding: '16px', 
                borderRadius: '8px', 
                borderLeft: '4px solid #FFD700',
                margin: '16px 0'
              }}>
                <p style={{ margin: 0, color: '#333', lineHeight: '1.6' }}>{data.note}</p>
              </div>
              <p style={{ margin: '16px 0', color: '#666' }}>
                Log in to your dashboard to view more details and respond if needed.
              </p>
            </>
          )
        };

      case 'agent_approved':
        return {
          subject: 'Agent Registration Approved - H E Direct Consultants',
          recipient: data.agentEmail,
          recipientName: data.agentName,
          body: (
            <>
              <div style={{ 
                background: '#d4edda', 
                padding: '16px', 
                borderRadius: '8px',
                marginBottom: '20px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <span style={{ fontSize: '24px' }}>✅</span>
                <span style={{ color: '#155724', fontWeight: 'bold' }}>Registration Approved!</span>
              </div>
              <p style={{ margin: '0 0 16px 0', color: '#333' }}>Dear {data.agentName},</p>
              <p style={{ margin: '0 0 16px 0', color: '#333', lineHeight: '1.6' }}>
                Congratulations! Your agent registration has been <strong>approved</strong>. 
                You can now start referring students and earning commissions.
              </p>
              <div style={{ 
                background: '#f8f9fa', 
                padding: '16px', 
                borderRadius: '8px',
                margin: '16px 0'
              }}>
                <p style={{ margin: '0 0 8px 0', color: '#666', fontSize: '14px' }}>Your Commission</p>
                <p style={{ margin: 0, color: '#1E3A5F', fontWeight: 'bold', fontSize: '24px' }}>Competitive Rates</p>
              </div>
              <p style={{ margin: '16px 0', color: '#666' }}>
                Log in to your agent dashboard to access your referral link and start growing your network. The more students you recruit, the higher your earnings.
              </p>
            </>
          )
        };

      case 'agent_rejected':
        return {
          subject: 'Agent Registration Update - H E Direct Consultants',
          recipient: data.agentEmail,
          recipientName: data.agentName,
          body: (
            <>
              <div style={{ 
                background: '#f8d7da', 
                padding: '16px', 
                borderRadius: '8px',
                marginBottom: '20px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <span style={{ fontSize: '24px' }}>❌</span>
                <span style={{ color: '#721c24', fontWeight: 'bold' }}>Registration Not Approved</span>
              </div>
              <p style={{ margin: '0 0 16px 0', color: '#333' }}>Dear {data.agentName},</p>
              <p style={{ margin: '0 0 16px 0', color: '#333', lineHeight: '1.6' }}>
                We regret to inform you that your agent registration has not been approved at this time.
              </p>
              {data.reason && (
                <div style={{ 
                  background: '#f8f9fa', 
                  padding: '16px', 
                  borderRadius: '8px',
                  borderLeft: '4px solid #dc3545',
                  margin: '16px 0'
                }}>
                  <p style={{ margin: '0 0 8px 0', color: '#666', fontSize: '14px' }}>Reason:</p>
                  <p style={{ margin: 0, color: '#333' }}>{data.reason}</p>
                </div>
              )}
              <p style={{ margin: '16px 0', color: '#666' }}>
                If you believe this is an error or would like to provide additional documentation, 
                please contact us at amin@he-direct.com.
              </p>
            </>
          )
        };

      case 'agent_compliance':
        return {
          subject: 'Agent Registration Under Review - H E Direct Consultants',
          recipient: data.agentEmail,
          recipientName: data.agentName,
          body: (
            <>
              <div style={{ 
                background: '#fff3cd', 
                padding: '16px', 
                borderRadius: '8px',
                marginBottom: '20px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <span style={{ fontSize: '24px' }}>🔍</span>
                <span style={{ color: '#856404', fontWeight: 'bold' }}>Compliance Review in Progress</span>
              </div>
              <p style={{ margin: '0 0 16px 0', color: '#333' }}>Dear {data.agentName},</p>
              <p style={{ margin: '0 0 16px 0', color: '#333', lineHeight: '1.6' }}>
                Your registration is currently under compliance review. Our team is verifying your credentials 
                and documentation to ensure the highest standards of service.
              </p>
              <p style={{ margin: '16px 0', color: '#666' }}>
                We will notify you once the review is complete. This typically takes 3-5 business days.
              </p>
            </>
          )
        };

      case 'application_status':
        return {
          subject: `Application Update: ${data.status} - H E Direct Consultants`,
          recipient: data.studentEmail,
          recipientName: data.studentName,
          body: (
            <>
              <p style={{ margin: '0 0 16px 0', color: '#333' }}>Dear {data.studentName},</p>
              <p style={{ margin: '0 0 16px 0', color: '#333', lineHeight: '1.6' }}>
                There&apos;s an update on your application for <strong>{data.courseName}</strong> at {data.universityName}.
              </p>
              <div style={{ 
                background: '#f8f9fa', 
                padding: '16px', 
                borderRadius: '8px',
                margin: '16px 0'
              }}>
                <p style={{ margin: '0 0 8px 0', color: '#666', fontSize: '14px' }}>Application Status</p>
                <p style={{ 
                  margin: 0, 
                  color: data.status === 'accepted' ? '#28a745' : data.status === 'rejected' ? '#dc3545' : '#1E3A5F',
                  fontWeight: 'bold',
                  fontSize: '18px',
                  textTransform: 'capitalize'
                }}>
                  {data.status.replace('_', ' ')}
                </p>
              </div>
              <p style={{ margin: '16px 0', color: '#666' }}>
                Log in to your dashboard to view the full details and next steps.
              </p>
            </>
          )
        };

      default:
        return {
          subject: 'Message from H E Direct Consultants',
          recipient: data.email,
          recipientName: data.name,
          body: <p>{data.message}</p>
        };
    }
  };

  const emailContent = getEmailContent();

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Eye className="w-5 h-5 text-teal-600" />
          <h3 className="text-gray-800 font-semibold">Email Preview</h3>
        </div>
        <Badge className="bg-blue-100 text-blue-600">
          Preview Mode
        </Badge>
      </div>

      {/* Email Meta */}
      <div className="bg-[#e9ecef]/50 rounded-lg p-4 space-y-2">
        <div className="flex items-center gap-3">
          <span className="text-gray-500 text-sm w-16">To:</span>
          <span className="text-gray-800 text-sm">{emailContent.recipientName} &lt;{emailContent.recipient}&gt;</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-gray-500 text-sm w-16">From:</span>
          <span className="text-gray-800 text-sm">H E Direct &lt;amin@he-direct.com&gt;</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-gray-500 text-sm w-16">Subject:</span>
          <span className="text-teal-600 text-sm font-medium">{emailContent.subject}</span>
        </div>
      </div>

      {/* Email Body Preview */}
      <div className="bg-white rounded-lg overflow-hidden">
        {/* Email Header */}
        <div style={{ 
          background: 'linear-gradient(135deg, #1E3A5F 0%, #0f172a 100%)', 
          padding: '24px',
          textAlign: 'center'
        }}>
          <h1 style={{ 
            color: '#FFD700', 
            margin: 0, 
            fontSize: '24px',
            fontWeight: 'bold'
          }}>
            H E Direct Consultants
          </h1>
          <p style={{ color: '#94a3b8', margin: '8px 0 0', fontSize: '14px' }}>
            Your Gateway to Global Education
          </p>
        </div>

        {/* Email Content */}
        <div style={{ padding: '24px', fontFamily: 'Arial, sans-serif' }}>
          {emailContent.body}
        </div>

        {/* Email Footer */}
        <div style={{ 
          background: '#f8f9fa', 
          padding: '20px', 
          textAlign: 'center',
          borderTop: '1px solid #e9ecef'
        }}>
          <p style={{ margin: '0 0 8px', color: '#666', fontSize: '14px' }}>
            H E Direct Consultants Team
          </p>
          <p style={{ margin: '0 0 4px', color: '#999', fontSize: '12px' }}>
            Main University Road, Peshawar, Pakistan
          </p>
          <p style={{ margin: 0, color: '#999', fontSize: '12px' }}>
            +44 7488 420729 | amin@he-direct.com
          </p>
        </div>
      </div>

      {/* Note about mocked emails */}
      <div className="flex items-start gap-2 p-3 bg-orange-500/10 border border-orange-500/30 rounded-lg">
        <AlertTriangle className="w-4 h-4 text-orange-600 flex-shrink-0 mt-0.5" />
        <p className="text-orange-700 text-xs">
          <strong>Note:</strong> Email sending is currently mocked. The email content will be logged to the server console instead of being sent.
        </p>
      </div>

      {/* Action Buttons */}
      <div className="flex items-center gap-3 pt-2">
        <Button
          onClick={handleConfirm}
          disabled={sending}
          className="flex-1 bg-teal-600 text-white hover:bg-teal-700 font-semibold"
          data-testid="confirm-send-email"
        >
          {sending ? (
            <>Sending...</>
          ) : (
            <>
              <Send className="w-4 h-4 mr-2" />
              Confirm & Send
            </>
          )}
        </Button>
        <Button
          onClick={onClose}
          variant="outline"
          className="border-gray-300 text-gray-600 hover:bg-gray-100"
        >
          <X className="w-4 h-4 mr-2" />
          Cancel
        </Button>
      </div>
    </div>
  );
};

export default EmailTemplatePreview;
